import * as Api from '@ViewModels';
import { IImpersonationContext } from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useSelectionState } from '../../../../hooks/useSelectionState';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { invalidateAllTagFilterQueries, useGenerateTagReportMutation } from '../../../../queries/Tag';
import { Button } from '../../../components-v2/Button';
import { ButtonGroup } from '../../../components-v2/ButtonGroup';
import { Input } from '../../../components-v2/Input';
import { Row } from '../../../components-v2/PageLayout';
import { Databoard, DataboardActions, DataboardFilters } from '../../../components/Databoard';
import { AddTagModal } from '../../../components/reporting/tags/AddTag';
import { GenerateTagNotificationsReportModal } from '../../../components/reporting/tags/GenerateTagNotificationsReport';
import { SearchIcon2 } from '../../../components/svgs/icons/SearchIcon2';
import { ContactsTableLoader } from '../shared/ContactsTableLoader';
import { styleSheet } from './styles';
import { CreateCategoryModal } from './TagCategoryModals/CreateCategoryModal';
import { DeleteCategoryModal } from './TagCategoryModals/DeleteCategoryModal';
import { UpdateCategoryModal } from './TagCategoryModals/UpdateCategoryModal';
import { TagsActionsBar } from './TagsActionsBar';
import { useTagsDataContext } from './TagsContexts/TagsDataContext';
import { useTagsSearchContext } from './TagsContexts/TagsSearchContext';
import { TagsSearchDescription } from './TagsSearchDescription';
import { TagsTable } from './TagsTable/TagsTable';
import { getTagSearchFromCriteria } from './TagsUtilities';

export const TagsMain: React.FC<{
	isAdminContext?: boolean;
	impersonationContext?: IImpersonationContext;
}> = props => {
	const { isAdminContext = false, impersonationContext } = props;
	const errorMessages = useErrorMessages();
	const { initialSearchValue, filter, debouncedSetSearchTerm, searchTerm } = useTagsSearchContext();
	const { tags, categories, selectedTag, selectedCategory, setSelectedCategory } = useTagsDataContext();

	// Table state
	const selectionState = useSelectionState({ rows: tags?.data?.map(item => item.id) });

	// Modals
	const [showAddCategoryModal, setShowAddCategoryModal] = React.useState(false);
	const [showAddTagModal, setShowAddTagModal] = React.useState(false);
	const [showDeleteCategoryModal, setShowDeleteCategoryModal] = React.useState(false);
	const [showUpdateCategoryModal, setShowUpdateCategoryModal] = React.useState(false);
	const [showGenerateReportModal, setShowGenerateReportModal] = React.useState(false);

	const searchInputRef = React.useRef<HTMLInputElement>(null);

	const generateTagReportMutation = useGenerateTagReportMutation({
		impersonationContext,
		onSuccess: () => {
			toast.success('Tag report has been initiated. You will receive an email when the report is ready.');
		},
		onError: error => {
			toast.error('An error occurred while trying to generate a tag report.');
			errorMessages.pushApiError(error);
		},
	});

	// Update states when query changes
	React.useEffect(() => {
		if (!filter?.criteria) return;
		if (searchInputRef.current) {
			searchInputRef.current.value = getTagSearchFromCriteria(filter.criteria);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location]);

	// Event handlers for tag actions
	const handleAddTagClick = () => {
		setShowAddTagModal(true);
	};

	const handleAddTagModalClose = (tag?: Api.TagViewModel, canceled?: boolean) => {
		setShowAddTagModal(false);
		if (!canceled && tag) {
			invalidateAllTagFilterQueries();
		}
	};

	const handleGenerateReportClick = () => {
		setShowGenerateReportModal(true);
	};

	const handleGenerateReportModalClose = (reportParams?: any, canceled?: boolean) => {
		setShowGenerateReportModal(false);

		if (!canceled && reportParams) {
			generateTagReportMutation.mutate(reportParams);
		}
	};

	const onSearch = () => {
		const value = searchInputRef.current?.value || '';
		debouncedSetSearchTerm(value);
		selectionState.reset();
	};

	const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter') {
			onSearch();
		}
	};

	const clearSearch = () => {
		if (searchInputRef.current) {
			searchInputRef.current.value = '';
		}
		if (searchTerm) debouncedSetSearchTerm('');
		selectionState.reset();
	};

	const handleCategoryCreated = (category: Api.ITagCategory | null) => {
		if (category) {
			setSelectedCategory(category);
		}
	};

	return (
		<>
			<Databoard styles={[styleSheet.databoard]}>
				<DataboardFilters>
					<Row styles={[styleSheet.searchControls]}>
						<div className={css(styleSheet.leftActions)}>
							<ButtonGroup>
								<Input
									placeholder='Search'
									defaultValue={initialSearchValue}
									onKeyDown={onSearchKeyDown}
									ref={searchInputRef}
									styles={[styleSheet.searchInput]}
								/>
								<Button onClick={onSearch} size='lg' styles={[styleSheet.searchButton]}>
									<SearchIcon2 />
								</Button>
							</ButtonGroup>
							{!!searchTerm && (
								<Button variant='secondary' onClick={clearSearch}>
									Clear search
								</Button>
							)}
						</div>
						<div className={css(styleSheet.rightActions)}>
							{!selectedCategory ? (
								<>
									<Button onClick={handleAddTagClick}>Add Tag</Button>
									<Button onClick={() => setShowAddCategoryModal(true)}>Add Category</Button>
								</>
							) : (
								<>
									<Button variant='destructive-secondary' onClick={() => setShowDeleteCategoryModal(true)}>
										Delete Category
									</Button>
									<Button onClick={() => setShowUpdateCategoryModal(true)}>Add Tag to Category</Button>
								</>
							)}
							{!isAdminContext ? <Button onClick={handleGenerateReportClick}>Generate Report</Button> : null}
						</div>
					</Row>
				</DataboardFilters>

				<DataboardFilters>
					<Row styles={[styleSheet.searchDescription]}>
						<TagsSearchDescription selectionState={selectionState} />
					</Row>
				</DataboardFilters>
				<DataboardActions>
					<TagsActionsBar
						impersonationContext={impersonationContext}
						isAdminContext={isAdminContext}
						selectionState={selectionState}
					/>
				</DataboardActions>
				{!tags?.data?.length && !categories?.data?.length && (tags.isFetching || categories.isFetching) ? (
					<ContactsTableLoader />
				) : (
					<TagsTable
						clearSearch={clearSearch}
						isAdminContext={isAdminContext}
						impersonationContext={impersonationContext}
						selectionState={selectionState}
						openUpdateCategoryModal={() => setShowUpdateCategoryModal(true)}
					/>
				)}
			</Databoard>
			<GenerateTagNotificationsReportModal
				modalProps={{
					isOpen: showGenerateReportModal,
					onRequestClose: handleGenerateReportModalClose,
				}}
			/>
			<AddTagModal
				modalProps={{
					isOpen: !!showAddTagModal,
					onRequestClose: handleAddTagModalClose,
				}}
				impersonationContext={impersonationContext}
			/>
			<CreateCategoryModal
				isOpen={showAddCategoryModal}
				onClose={() => setShowAddCategoryModal(false)}
				impersonationContext={impersonationContext}
				onCategoryCreated={handleCategoryCreated}
			/>
			<UpdateCategoryModal
				isOpen={showUpdateCategoryModal || !!selectedTag}
				onClose={() => setShowUpdateCategoryModal(false)}
				impersonationContext={impersonationContext}
			/>
			<DeleteCategoryModal
				isOpen={showDeleteCategoryModal}
				onClose={() => setShowDeleteCategoryModal(false)}
				impersonationContext={impersonationContext}
			/>
		</>
	);
};
